import {
  isSessionIdleExempt, SESSION_IDLE_TIMEOUT_MS, SESSION_IDLE_ACTIVITY_INTERVAL_MS,
  SESSION_IDLE_RETRY_MS, type SessionIdleStatus,
} from "@/lib/auth/session-idle"

export class IdleSessionExpiredError extends Error {}

export async function requestSessionIdleStatus(activity: boolean, keepalive = false): Promise<SessionIdleStatus> {
  const response = await fetch("/api/auth/session-idle", {
    method: "POST", credentials: "same-origin", cache: "no-store", keepalive,
    headers: { "Content-Type": "application/json" }, body: JSON.stringify({ activity }),
  })
  if (response.status === 401) throw new IdleSessionExpiredError()
  if (!response.ok) throw new Error("Não foi possível verificar a sessão.")
  return response.json()
}

type EventSource = Pick<EventTarget, "addEventListener" | "removeEventListener">
type IdleMonitorOptions = {
  userId: string
  onExpire: () => void | Promise<void>
  check?: typeof requestSessionIdleStatus
  events?: EventSource
  visibility?: EventSource & { visibilityState: string }
  now?: () => number
  setTimer?: (callback: () => void, delay: number) => number
  clearTimer?: (id: number) => void
}

/** Only user input records activity. Refreshes, checks, focus and visibility never do. */
export function startSessionIdleMonitor(options: IdleMonitorOptions): () => void {
  if (isSessionIdleExempt(options.userId)) return () => {}
  const events = options.events ?? window
  const visibility = options.visibility ?? document
  const now = options.now ?? Date.now
  const setTimer = options.setTimer ?? ((callback, delay) => window.setTimeout(callback, delay))
  const clearTimer = options.clearTimer ?? (id => window.clearTimeout(id))
  const check = options.check ?? requestSessionIdleStatus
  let stopped = false
  let inFlight = false
  let pendingActivity = false
  let lastActivityRequest = -Infinity
  let lastInteraction = -Infinity
  let deadline = now() + SESSION_IDLE_TIMEOUT_MS
  let deadlineTimer: number | undefined
  let activityTimer: number | undefined
  const activityEvents = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"]

  const stop = () => {
    stopped = true
    if (deadlineTimer !== undefined) clearTimer(deadlineTimer)
    if (activityTimer !== undefined) clearTimer(activityTimer)
    activityEvents.forEach(type => events.removeEventListener(type, onActivity, true))
    events.removeEventListener("focus", onResume)
    events.removeEventListener("pageshow", onResume)
    events.removeEventListener("online", onResume)
    events.removeEventListener("pagehide", onPageHide)
    visibility.removeEventListener("visibilitychange", onResume)
  }
  const expire = () => {
    if (stopped) return
    stop()
    void Promise.resolve(options.onExpire()).catch(() => {})
  }
  const scheduleCheck = (delay: number) => {
    if (deadlineTimer !== undefined) clearTimer(deadlineTimer)
    deadlineTimer = setTimer(() => { deadlineTimer = undefined; void request(false) }, Math.max(0, delay))
  }
  const scheduleActivity = () => {
    if (stopped || !pendingActivity || activityTimer !== undefined) return
    const delay = Math.max(0, lastActivityRequest + SESSION_IDLE_ACTIVITY_INTERVAL_MS - now())
    activityTimer = setTimer(() => {
      activityTimer = undefined
      void request(true)
    }, delay)
  }
  const request = async (activity: boolean) => {
    if (stopped || inFlight) return
    inFlight = true
    if (activity) { pendingActivity = false; lastActivityRequest = now() }
    try {
      const status = await check(activity)
      if (stopped) return
      if (status.expired) { expire(); return }
      if (status.exempt) { stop(); return }
      const remaining = Date.parse(status.expiresAt ?? "") - Date.parse(status.serverNow)
      if (!Number.isFinite(remaining)) throw new Error("Resposta inválida de sessão.")
      if (remaining <= 0) { expire(); return }
      // Use the server's remaining interval rather than depending on the device clock.
      deadline = now() + remaining
      scheduleCheck(remaining)
    } catch (error) {
      if (stopped) return
      if (error instanceof IdleSessionExpiredError) { expire(); return }
      if (activity) pendingActivity = true
      const offlineDeadline = Math.max(deadline, lastInteraction + SESSION_IDLE_TIMEOUT_MS)
      if (now() >= offlineDeadline) { expire(); return }
      scheduleCheck(Math.min(SESSION_IDLE_RETRY_MS, offlineDeadline - now()))
    } finally {
      inFlight = false
      if (!stopped) scheduleActivity()
    }
  }
  function onActivity(event: Event) {
    if (!event.isTrusted || stopped) return
    lastInteraction = now()
    pendingActivity = true
    scheduleActivity()
  }
  function onResume() {
    if (visibility.visibilityState === "visible") void request(false)
  }
  function onPageHide() {
    // Flush only pending real input; closing a tab on its own is not activity.
    if (pendingActivity && !stopped) void check(true, true).catch(() => {})
  }

  activityEvents.forEach(type => events.addEventListener(type, onActivity, { capture: true, passive: true }))
  events.addEventListener("focus", onResume)
  events.addEventListener("pageshow", onResume)
  events.addEventListener("online", onResume)
  events.addEventListener("pagehide", onPageHide)
  visibility.addEventListener("visibilitychange", onResume)
  void request(false)
  return stop
}
